import { AuthContextType } from "src/contexts/AuthContext";
import { AppDispatch } from "src/store";
import { SegmentedMessage } from 'sms-segments-calculator';
import { MessageType } from 'src/constants/types';
import { AxiosResponse } from "axios";
import { clearAuthTokens } from "src/api/axiosClient/tokenStorage";
import { triggerBlobDownload } from "src/helpers/files";

export const convertHtmlToText = (html: string | null, placeholder = "") => {
  const container = document.createElement("div");
  container.innerHTML = html?.replaceAll("<br>", "\n").replaceAll("</p>", "\n").replaceAll("<br/>", "\n") || "";
  const customFields = container.querySelectorAll("span.mention[data-id]");
  customFields.forEach((customField) => {
    // Only process actual custom fields, not short links
    if (customField.classList.contains('mention-link')) {
      // This is a short link - leave it as is
      return;
    }
    customField.replaceWith("{{" + customField.getAttribute("data-id") + "}}");
  });
  const result = container.innerText.replace(/[^\S\n\r]*\n$/, "") || placeholder;

  return result;
};

export const generateUrlWithCompany = (to: string, companyId?: string): string => {
  if (companyId) {
    const url = new URL(to, window.location.origin);
    url.searchParams.set('companyId', companyId);
    return url.pathname + url.search;
  }
  return to;
};

export const SPINNER_COLOR = '#1976d2';

const showCompanySwitchOverlay = () => {
  document.documentElement.style.overflow = 'hidden';
  if (document.getElementById('bhi-company-switch-overlay')) {
    return;
  }
  const overlay = document.createElement('div');
  overlay.id = 'bhi-company-switch-overlay';
  overlay.setAttribute('role', 'progressbar');
  overlay.setAttribute('aria-label', 'Switching company');
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;' +
    'justify-content:center;background:#fff;cursor:wait;';
  const spinner = document.createElement('div');
  spinner.style.cssText =
    'width:40px;height:40px;border-radius:50%;border:4px solid rgba(0,0,0,0.15);' +
    `border-top-color:${SPINNER_COLOR};animation:bhi-switch-spin 0.8s linear infinite;` +
    `animation-delay:-${Date.now() % 800}ms;`;
  const style = document.createElement('style');
  style.textContent = '@keyframes bhi-switch-spin{to{transform:rotate(360deg)}}';
  overlay.appendChild(style);
  overlay.appendChild(spinner);
  document.body.appendChild(overlay);
};

export const hideAppBootSpinner = () => {
  document.getElementById('bhi-boot-spinner')?.remove();
};

export const isCompanyScopedPage = (pathname: string): boolean =>
  pathname.startsWith('/campaigns') ||
  pathname.startsWith('/campaign-builder') ||
  pathname.startsWith('/campaign-config-builder') ||
  pathname.startsWith('/groups') ||
  pathname.startsWith('/group/');

export const switchCompany = (companyId: string) => {
  showCompanySwitchOverlay();
  localStorage.setItem('bhiCurrentCompany', companyId);

  const { pathname, search } = window.location;

  if (isCompanyScopedPage(pathname)) {
    window.location.replace(`/?companyId=${encodeURIComponent(companyId)}`);
  } else {
    const newSearchParams = new URLSearchParams(search);
    newSearchParams.set('companyId', companyId);
    window.location.assign(`${pathname}?${newSearchParams.toString()}`);
  }

  const dismissOverlay = () => {
    document.getElementById('bhi-company-switch-overlay')?.remove();
    document.documentElement.style.overflow = '';
    window.removeEventListener('pointerdown', dismissOverlay, true);
    window.removeEventListener('keydown', dismissOverlay, true);
    window.removeEventListener('wheel', dismissOverlay, true);
  };
  window.addEventListener('pointerdown', dismissOverlay, true);
  window.addEventListener('keydown', dismissOverlay, true);
  window.addEventListener('wheel', dismissOverlay, true);
};

export const handleLogout = (authContext: AuthContextType | null, dispatch: AppDispatch) => {
  authContext?.logout();
  clearAuthTokens();
  localStorage.removeItem('bhiUiSettings');
  dispatch({ type: 'logout' });
};

export const MAX_SMS_PARTS = 8;

export const getSmsPartsCount = (
  messageType: MessageType,
  messageText: string | undefined | null,
  hasAttachments: boolean
): number => {
  if (messageType === "sms") {
    if (!messageText || !convertHtmlToText(messageText).length) {
      return 0;
    }

    const segmentedMessage = new SegmentedMessage(convertHtmlToText(messageText));
    return segmentedMessage.segmentsCount;
  }

  if (messageType === "mms") {
    if (!messageText || !convertHtmlToText(messageText).length) {
      return hasAttachments ? 1 : 0;
    }

    return Math.ceil(convertHtmlToText(messageText).length / 1600);
  }

  return 0;
};

export const isMessageTooLong = (
  messageType: MessageType,
  messageText: string | undefined | null,
  hasAttachments: boolean
): boolean => {
  const parts = getSmsPartsCount(messageType, messageText, hasAttachments);
  return parts > MAX_SMS_PARTS;
};

export const capitalizeFirstLetter = (string: string | undefined | null): string | undefined | null => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const extractFilenameFromContentDisposition = (contentDisposition: string): string | null => {
  if (!contentDisposition) return null;

  const parts = contentDisposition.split('filename=');
  if (parts.length < 2) return null;

  return parts[1].replace(/^["']|["']$/g, '');
};

export const handleDownload = (response: AxiosResponse<Blob>) => {
  const contentDisposition = response.headers['content-disposition'];
  const filename = extractFilenameFromContentDisposition(contentDisposition) || '';

  triggerBlobDownload(response.data, filename);
};